import { NestedTreeControl } from '@angular/cdk/tree'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormBuilder, FormControl } from '@angular/forms'
import { MatTreeNestedDataSource } from '@angular/material/tree'
import { normalizeString } from '@shared/utils/degts-utils'
import { FormFieldComponent } from '@widgets/form-field/form-field.component'
import { Subscription } from 'rxjs'
import { TreeSearchService } from '../service/tree-search.service'
import { TreeSearchProvider } from './tree-search-provider'

export interface TreeNode {
  id: number | string
  name: string
  parent?: TreeNode
  children?: TreeNode[]
  selected?: boolean
  visible?: boolean
}

@Component({
  selector: 'app-tree-search-component',
  templateUrl: './tree-search.component.html',
  styleUrls: ['./tree-search.component.scss'],
})
export class TreeSearchComponent<T extends { id: number | string }>
  extends FormFieldComponent
  implements OnInit, OnDestroy {
  @Input()
  placeholder = 'Buscar...'

  @Input()
  label: string

  @Input()
  provider: TreeSearchProvider<T>

  @Input()
  private viewOnly = false

  @Input()
  private showControls = true

  @Input()
  private allExpanded = false

  @Input()
  height = 40

  @Input()
  private nullable = false

  @Output()
  formConfirm = new EventEmitter()

  @Output()
  formCancel = new EventEmitter()

  parentNodes: Array<T>
  childNodes: Array<T>
  filteredNodes: Array<T>

  inputControl: FormControl
  inputChanges: Subscription
  incluirLabel: string

  dataSource = new MatTreeNestedDataSource<TreeNode>()
  treeControl = new NestedTreeControl<TreeNode>((node) => this.getChildren(node))

  private inputForm = new FormBuilder()

  constructor(protected treeService: TreeSearchService) {
    super()
  }

  ngOnInit() {
    if (this.control && !this.control?.value) {
      this.control.setValue([], { emitEvent: false })
    }
    this.inputControl = this.inputForm.control('')
    this.childNodes = this.provider?.options
    this.filteredNodes = this.childNodes ? [...this.childNodes] : []
    this.parentNodes = this.treeService.getParents(this.provider, this.childNodes)
    this.generateTree()
    this.incluirLabel = this.getSelected()?.length > 0 ? `INCLUIR (${this.getSelected().length})` : 'INCLUIR'
    this.updateItems('')
    this.allExpanded ? this.treeControl.expandAll() : this.treeControl.collapseAll()
    this.inputChanges = this.inputControl.valueChanges.subscribe((value) => this.filterItems(this.inputControl.value))
  }

  filterItems(filter: string) {
    this.filteredNodes = this.childNodes?.filter((option) =>
      normalizeString(this.provider.description(option)).includes(normalizeString(filter))
    )
    this.updateItems(filter)
    filter ? this.expandItems() : this.treeControl?.collapseAll()
  }

  generateTree() {
    this.dataSource.data = this.parentNodes?.map(
      (parent: T): TreeNode => {
        return ({
          id: parent?.id,
          descricao: this.provider.description(parent),
          children: this.treeService.getChildren(this.provider, this.childNodes, parent),
          visible: true,
        } as unknown) as TreeNode
      }
    )
    this.dataSource.data?.forEach((parent) => {
      parent?.children?.forEach((child) => {
        child.selected =
          this.control.value.filter((node) => node.parent.id === parent.id && node.id === child.id).length != 0
      })
    })
    this.treeControl.dataNodes = this.dataSource.data
  }

  updateItems(filter: string) {
    this.dataSource.data?.forEach((parentNode) => {
      parentNode.children?.forEach((childNode) => {
        if (filter === '') {
          childNode.visible = true
        } else {
          childNode.visible = this.filteredNodes.some(
            (node) => this.provider.parent(node)?.id === parentNode.id && node.id === childNode.id
          )
        }
      })
    })
  }

  expandItems() {
    this.treeControl.dataNodes.forEach((node) => {
      if (this.canShowChildren(null, node)) {
        this.treeControl.expand(node)
      } else {
        this.treeControl.collapse(node)
      }
    })
  }

  updateAllSelected(): void {
    this.dataSource.data.forEach((node) => {
      node.selected = this.allSelected(node)
    })
    this.incluirLabel = this.getSelected().length > 0 ? `INCLUIR (${this.getSelected().length})` : 'INCLUIR'
  }

  allSelected(node: TreeNode): boolean {
    return node.children != null && node.children.every((child) => child.selected)
  }

  someSelected(node: TreeNode): boolean {
    if (node.children == null) {
      return false
    }
    return node.children.filter((child) => child.selected).length > 0 && !this.allSelected(node)
  }

  setAll(node: TreeNode, selected: boolean): void {
    if (node.children == null) {
      return
    }
    node.children.forEach((child) => (child.selected = selected))
    if (selected) {
      this.treeControl.expand(node)
    }
    this.incluirLabel = this.getSelected().length > 0 ? `INCLUIR (${this.getSelected().length})` : 'INCLUIR'
  }

  rebuildControlValue() {
    var selected = this.getSelected()
    this.control.setValue(selected, { emitEvent: false })
  }

  getSelected() {
    return this.dataSource.data
      ?.map((node) => node.children)
      .reduce((prevSet, set) => prevSet.concat(set))
      .filter((child) => child.selected)
  }

  canShowChildren = (_: number, node: TreeNode) => {
    if (node.children) {
      return node.children.filter((child) => child.visible).length > 0
    }
    return false
  }

  getChildren = (node: TreeNode) => {
    return (node?.children as unknown) as TreeNode[]
  }

  get minHeight() {
    return this.height - 14
  }

  onInclude() {
    this.rebuildControlValue()
    this.formConfirm.emit(true)
  }

  onCancel() {
    this.generateTree()
    this.formCancel.emit(true)
  }

  ngOnDestroy() {
    this.inputChanges.unsubscribe()
  }

  isViewOnly(): boolean {
    return this.viewOnly
  }

  isDisabled(node: TreeNode): boolean {
    return this.isViewOnly() || !this.canShowChildren(null, node) || this.inputControl.value != ''
  }

  canShowControls(): boolean {
    return this.showControls
  }
}
